import { useState } from 'react';
import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from '@mui/material';
import { AccountEntryService } from 'api';
import { isSuccessful } from 'api/utils';
import { StyledButton } from 'components/Buttons';
import { LoaderFullScreen } from 'components/Loaders/LoaderFullScreen';
import { useTranslation } from 'react-i18next';
import { appToast } from 'utils/toast.utils';
import * as S from '../../InvoiceAllocations.css';

type Props = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  accountEntryId: number;
  accountEntriesLoading: boolean;
};

export const RejectAllocationDialog = ({
  open,
  onClose,
  onSubmit,
  accountEntryId,
  accountEntriesLoading,
}: Props) => {
  const [reason, setReason] = useState('');
  const { t } = useTranslation(['misc', 'validation']);

  const { loading, rejectAccountEntry } =
    AccountEntryService.useRejectAccountEntry();

  const handleSubmit = async () => {
    const res = await rejectAccountEntry(accountEntryId, reason);
    isSuccessful(res) &&
      appToast.success(t('message.deleteSuccess', { ns: 'misc' }));
    await onSubmit();
    onClose();
  };

  const isBackdropVisible: boolean = loading || accountEntriesLoading;

  return (
    <S.StyledDialog
      open={open}
      onClose={onClose}
      TransitionProps={{ onExited: () => setReason('') }}
    >
      <LoaderFullScreen loading={isBackdropVisible} />
      <DialogTitle>
        {t('modal.rejectAllocationHeading', { ns: 'misc' })}
      </DialogTitle>
      <DialogContent dividers>
        <DialogContentText>
          {t('modal.rejectAllocationBody', { ns: 'misc' })}
        </DialogContentText>
        <TextField
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          autoFocus
          margin='normal'
          minRows={3}
          multiline
          label={t('modal.rejectAllocationReasonLabel', { ns: 'misc' })}
          fullWidth
          variant='outlined'
          error={!reason.length}
          helperText={
            !reason.length ? t('required', { ns: 'validation' }) : null
          }
        />
      </DialogContent>
      <DialogActions>
        <StyledButton disabled={!reason.length} $primary onClick={handleSubmit}>
          {t('button.reject', { ns: 'misc' })}
        </StyledButton>
        <StyledButton onClick={onClose}>
          {t('button.cancel', { ns: 'misc' })}
        </StyledButton>
      </DialogActions>
    </S.StyledDialog>
  );
};
